import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Global business settings — admin-editable, apply to all customers.
 *
 *   - Saver Discount %s (the four bundle discount tiers)
 *   - Order email recipients
 *
 * Document-management surfaces (Terms of Trade PDF, Acceptance Log, Quality
 * Assurance Certifications) live in `GlobalDocsPanel.jsx` and render BELOW
 * the user table.
 *
 * Per-customer toggles (BCC, prepay opt-in + label) and per-product labels
 * (sizeLabel) live elsewhere — see CustomerOverridesModal / Product Edit form.
 *
 * Rendered on the User Management page above the user table (admin only).
 */
const GlobalSettingsPanel = () => {
  const [discounts, setDiscounts] = useState({
    small_single_pct: 10, small_box_pct: 10, large_single_pct: 10, large_box_pct: 10,
    small_pallet_pct: 10, large_pallet_pct: 10,
  });
  const [recipientsText, setRecipientsText] = useState('');
  const [savingDiscounts, setSavingDiscounts] = useState(false);
  const [discountSaveStatus, setDiscountSaveStatus] = useState('idle');

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setDiscounts(res.data);
      setRecipientsText((res.data.orderRecipients || []).join(', '));
    } catch (e) {
      console.error('Error fetching settings:', e);
    }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const saveDiscounts = async () => {
    setSavingDiscounts(true);
    try {
      const orderRecipients = recipientsText
        .split(/[,\n]/).map((e) => e.trim()).filter(Boolean);
      // Every tier that has an input on this form gets sent explicitly (as a
      // number, defaulting to 0). Keys not defined here are ignored server-side.
      const numericPayload = {};
      [
        'small_single_pct', 'small_box_pct', 'large_single_pct', 'large_box_pct',
        'small_pallet_pct', 'large_pallet_pct',
      ].forEach((k) => { numericPayload[k] = parseFloat(discounts[k]) || 0; });
      await axios.put(`${API}/settings`, { ...numericPayload, orderRecipients });
      setDiscountSaveStatus('saved');
      setTimeout(() => setDiscountSaveStatus('idle'), 2500);
    } catch (e) {
      console.error('Error saving settings:', e);
      setDiscountSaveStatus('error');
      setTimeout(() => setDiscountSaveStatus('idle'), 2500);
    } finally {
      setSavingDiscounts(false);
    }
  };

  const tierFields = [
    { key: 'small_single_pct', label: 'Small Single (1–7× 1kg tubs)' },
    { key: 'small_box_pct',    label: 'Small Box (8–39 × 1kg tubs)' },
    { key: 'small_pallet_pct', label: 'Small Pallet Layer (5+ boxes)' },
    { key: 'large_single_pct', label: 'Large Single (1–5× 2kg tubs)' },
    { key: 'large_box_pct',    label: 'Large Box (6–29 × 2kg tubs)' },
    { key: 'large_pallet_pct', label: 'Large Pallet Layer (5+ boxes)' },
  ];

  return (
    <div className="space-y-6 mb-8">
      <div className="bg-surface border border-border-color rounded-xl p-6" data-testid="discount-settings">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold font-heading text-gold">Saver Discount Settings</h2>
            <p className="text-sm text-text-secondary mt-1">
              Default customer-facing discount applied to total RRP when building a saver.
              Override per customer via that customer&apos;s Overrides modal.
            </p>
          </div>
          <button
            onClick={saveDiscounts}
            disabled={savingDiscounts}
            data-testid="settings-save-button"
            className="px-4 py-2 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingDiscounts ? 'Saving…' : 'Save Settings'}
          </button>
        </div>
        {discountSaveStatus === 'saved' && (
          <p className="text-xs text-success mb-2" data-testid="settings-save-status">Saved.</p>
        )}
        {discountSaveStatus === 'error' && (
          <p className="text-xs text-danger mb-2" data-testid="settings-save-status-error">
            Could not save settings.
          </p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {tierFields.map(({ key, label }) => (
            <div key={key}>
              <label className="text-sm text-text-secondary mb-1.5 block">{label}</label>
              <div className="relative">
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  max="100"
                  value={discounts[key]}
                  onChange={(e) => setDiscounts({ ...discounts, [key]: e.target.value })}
                  data-testid={`discount-${key}-input`}
                  className="w-full pl-3 pr-7 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary text-sm">%</span>
              </div>
            </div>
          ))}
        </div>
        <div>
          <label className="text-sm text-text-secondary mb-2 block">Order email recipients (comma-separated)</label>
          <textarea
            rows={2}
            value={recipientsText}
            onChange={(e) => setRecipientsText(e.target.value)}
            placeholder="e.g. aaron@raiseys.co.nz, kane@raiseys.co.nz"
            data-testid="order-recipients-textarea"
            className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
          />
          <p className="text-xs text-text-secondary mt-1">
            Customers click &quot;Submit Order&quot; to email their saved savers to this list.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GlobalSettingsPanel;
